/**
 * Intranet (ADR 0077): comunicados, novidades, agenda, políticas e normativas e links úteis da
 * página Início. Quem tem `intranet.content.manage` cadastra e publica; qualquer pessoa logada lê o
 * que está publicado. Textos em pt-BR (obrigatório) e en (opcional; sem tradução, vale o pt-BR).
 */
import { z } from 'zod';

export const INTRANET_POST_KINDS = ['announcement', 'news', 'event', 'policy'] as const;
export type IntranetPostKind = (typeof INTRANET_POST_KINDS)[number];

export const INTRANET_POST_STATUSES = ['draft', 'published', 'archived'] as const;
export type IntranetPostStatus = (typeof INTRANET_POST_STATUSES)[number];

export const INTRANET_EVENT_KINDS = ['event', 'training', 'date'] as const;
export type IntranetEventKind = (typeof INTRANET_EVENT_KINDS)[number];

export const INTRANET_LINK_CATEGORIES = [
  'tools',
  'policies',
  'benefits',
  'documents',
  'support',
] as const;
export type IntranetLinkCategory = (typeof INTRANET_LINK_CATEGORIES)[number];

/** Texto da intranet: pt-BR obrigatório (quando pedido), en opcional. */
export type IntranetText = { 'pt-BR': string; en?: string };

/** O texto no idioma da pessoa; sem tradução, o português. */
export function intranetText(text: Partial<IntranetText> | null | undefined, lang: string): string {
  if (!text) return '';
  const en = text.en?.trim();
  return lang === 'en' && en ? en : (text['pt-BR'] ?? '');
}

const blank = (v: string | null | undefined) => (v && v.trim() ? v.trim() : undefined);

const text = (max: number, required: boolean) =>
  z
    .object({
      'pt-BR': required
        ? z.string().trim().min(1, 'Escreva em português').max(max)
        : z.string().trim().max(max).optional(),
      en: z.string().trim().max(max).optional().nullable(),
    })
    .transform((t) => {
      const out: Partial<IntranetText> = {};
      const pt = blank(t['pt-BR']);
      const en = blank(t.en);
      if (pt) out['pt-BR'] = pt;
      if (en) out.en = en;
      return out;
    });

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null));

/** Endereço externo (https) ou uma tela do próprio sistema (começa com /). */
const url = z
  .string()
  .trim()
  .max(490)
  .regex(
    /^(https:\/\/|\/)\S+$/,
    'Use um endereço que comece com https:// ou uma tela do sistema (/…)',
  );

const instant = z
  .string()
  .datetime({ offset: true })
  .optional()
  .nullable()
  .transform((v) => v ?? null);

export const intranetPostInput = z
  .object({
    kind: z.enum(INTRANET_POST_KINDS),
    title: text(200, true),
    summary: text(500, false).default({}),
    body: text(20000, false).default({}),
    ownerArea: optionalText(120),
    urgent: z.boolean().default(false),
    displayFrom: instant,
    displayUntil: instant,
    startsAt: instant,
    endsAt: instant,
    allDay: z.boolean().default(false),
    location: optionalText(200),
    eventKind: z.enum(INTRANET_EVENT_KINDS).optional().nullable(),
    version: optionalText(40),
    effectiveFrom: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional()
      .nullable()
      .transform((v) => v ?? null),
    documentUrl: url
      .optional()
      .nullable()
      .or(z.literal(''))
      .transform((v) => v || null),
  })
  .superRefine((p, ctx) => {
    if (p.kind === 'event' && !p.startsAt)
      ctx.addIssue({ code: 'custom', path: ['startsAt'], message: 'Informe o início do evento' });
    if (p.startsAt && p.endsAt && p.endsAt < p.startsAt)
      ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'O fim vem depois do início' });
    if (p.displayFrom && p.displayUntil && p.displayUntil <= p.displayFrom)
      ctx.addIssue({
        code: 'custom',
        path: ['displayUntil'],
        message: 'O fim da exibição vem depois do início',
      });
  })
  .transform((p) => ({
    ...p,
    // Cada tipo guarda só os campos dele (o banco confere).
    urgent: p.kind === 'announcement' ? p.urgent : false,
    startsAt: p.kind === 'event' ? p.startsAt : null,
    endsAt: p.kind === 'event' ? p.endsAt : null,
    allDay: p.kind === 'event' ? p.allDay : false,
    location: p.kind === 'event' ? p.location : null,
    eventKind: p.kind === 'event' ? (p.eventKind ?? 'event') : null,
    version: p.kind === 'policy' ? p.version : null,
    effectiveFrom: p.kind === 'policy' ? p.effectiveFrom : null,
  }));
export type IntranetPostInput = z.input<typeof intranetPostInput>;

export const intranetLinkInput = z.object({
  label: text(120, true),
  url,
  category: z.enum(INTRANET_LINK_CATEGORIES),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(100),
  isActive: z.boolean().default(true),
});
export type IntranetLinkInput = z.input<typeof intranetLinkInput>;

/** Imagem da publicação: JPG, PNG ou WebP. A tela reduz antes de enviar (lado maior). */
export const INTRANET_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const INTRANET_IMAGE_MAX_BYTES = 3 * 1024 * 1024;
export const INTRANET_IMAGE_SIDE = 1800;
